import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaChevronRight, FaRedo, FaTrophy } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { HomePhaseShell } from "@/components/v2/home/HomePhaseShell";
import { HomeSecondarySection } from "@/components/v2/home/HomeSecondarySection";
import { MatchResultAdminForm, MatchResultScoreboard } from "@/components/v2/home/HomePostMatchCard";

interface HomeCompletedCardProps {
  isAdmin: boolean;
  recapDateLabel: string;
  winner: "A" | "B" | "Draw" | null;
  scoreA: number | null;
  scoreB: number | null;
  teaserLines: string[];
  showEditResult: boolean;
  editScoreA: string;
  editScoreB: string;
  editWinner: "A" | "B" | "Draw" | null;
  onEditScoreAChange: (value: string) => void;
  onEditScoreBChange: (value: string) => void;
  onEditWinnerChange: (value: "A" | "B" | "Draw" | null) => void;
  onLogResult: () => void;
  showLoopReset: boolean;
  onStartNextWeek: () => void;
  startingNextWeek: boolean;
  historySlot?: ReactNode;
  commentsSlot?: ReactNode;
}

export function HomeCompletedCard({
  isAdmin,
  recapDateLabel,
  winner,
  scoreA,
  scoreB,
  teaserLines,
  showEditResult,
  editScoreA,
  editScoreB,
  editWinner,
  onEditScoreAChange,
  onEditScoreBChange,
  onEditWinnerChange,
  onLogResult,
  showLoopReset,
  onStartNextWeek,
  startingNextWeek,
  historySlot,
  commentsSlot,
}: HomeCompletedCardProps) {
  const { t } = useTranslation();

  const winnerLabel =
    winner === "A"
      ? t("v2.home.wonTeamA")
      : winner === "B"
        ? t("v2.home.wonTeamB")
        : winner === "Draw"
          ? t("v2.home.matchDraw")
          : t("v2.home.resultPending");

  const completedSubtitle = isAdmin
    ? t("v2.home.completedCtaAdmin")
    : showLoopReset
      ? t("v2.home.completedCtaMember")
      : t("v2.home.completedDesc");

  return (
    <HomePhaseShell
      eyebrow={t("v2.home.phaseEyebrowCompleted")}
      title={t("v2.home.postMatchResultTitle")}
      meta={
        <span>
          {recapDateLabel ? `${recapDateLabel} · ` : ""}
          {completedSubtitle}
        </span>
      }
      secondary={
        <div className="space-y-4">
          {teaserLines.length > 0 && (
            <HomeSecondarySection label={t("v2.home.highlights")} defaultOpen>
              <ul className="space-y-1 text-sm">
                {teaserLines.map((line, i) => (
                  <li key={i} className="font-medium">
                    {line}
                  </li>
                ))}
              </ul>
            </HomeSecondarySection>
          )}

          {commentsSlot}
          {historySlot}

          <Link
            to="/v2/stats"
            className={cn(
              buttonVariants({ variant: "outline" }),
              "flex w-full min-h-[44px] items-center justify-between"
            )}
          >
            <span>{t("v2.home.goToStats")}</span>
            <FaChevronRight className="text-xs shrink-0" />
          </Link>
        </div>
      }
      adminSection={
        isAdmin ? (
          <div className="space-y-4">
            {showEditResult && (
              <MatchResultAdminForm
                scoreA={editScoreA}
                scoreB={editScoreB}
                winner={editWinner}
                onScoreAChange={onEditScoreAChange}
                onScoreBChange={onEditScoreBChange}
                onWinnerChange={onEditWinnerChange}
                onSave={onLogResult}
                saveLabel={t("v2.home.updateResult")}
              />
            )}

            <Button
              onClick={onStartNextWeek}
              disabled={startingNextWeek}
              className="w-full min-h-[48px] bg-amber-600 text-white hover:bg-amber-600/90 font-bold shadow-sm"
            >
              <FaRedo className="mr-1.5" />
              {startingNextWeek ? t("common.loading") : t("v2.home.startNextWeek")}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center">
            {t("v2.home.adminOnlyStartWeek")}
          </p>
        )
      }
      adminZone={
        isAdmin
          ? {
              title: t("v2.home.adminZoneNextWeekTitle"),
              description: t("v2.home.adminZoneNextWeekDesc"),
              actionRequired: true,
            }
          : undefined
      }
    >
      <MatchResultScoreboard
        mode="display"
        light={scoreA}
        dark={scoreB}
        teamALabel={t("v2.home.teamA")}
        teamBLabel={t("v2.home.teamB")}
      />
      {winner && (
        <p className="flex items-center justify-center gap-1.5 text-sm font-semibold text-soccer-primary">
          <FaTrophy className="h-3.5 w-3.5" /> {winnerLabel}
        </p>
      )}
    </HomePhaseShell>
  );
}
